import React from 'react';
import IconWithErrorBoundary, { IconProps } from '@minecraft.icon';
import { DesignSystemBorderColor } from '@minecraft.themes';
import {
  BasicContainer,
  BodyText,
  ButtonAsLink,
  ButtonAsLinkProps,
  ButtonAsWrapper,
  FlexCol,
  FlexRow,
  H4Text,
} from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';

export type ToastButtonProps = {
  id: string;
  label: ButtonAsLinkProps['label'];
  to: ButtonAsLinkProps['to'];
  variant?: ButtonAsLinkProps['variant'];
  target?: ButtonAsLinkProps['target'];
  disabled?: ButtonAsLinkProps['disabled'];
  'data-heap-id'?: ButtonAsLinkProps['data-heap-id'];
};

const VARIANTS = {
  success: 'success',
  danger: 'danger',
  warning: 'warning',
} as const;

type ToastVariant = keyof typeof VARIANTS;

export interface ToastProps {
  variant?: ToastVariant;
  handleClose?: () => void;
  title?: string;
  description?: string | React.ReactNode;
  'data-testid'?: string;
  'data-qa-id'?: string;
  buttons?: Array<ToastButtonProps>;
}

const toastIconMap: Record<
  ToastVariant,
  { icon: IconProps['name']; themeColor: IconProps['themeColor']; borderColor: DesignSystemBorderColor }
> = {
  success: {
    icon: 'check-circle' as const,
    themeColor: 'success' as const,
    borderColor: 'success' as const,
  },
  danger: {
    icon: 'warning-banner' as const,
    themeColor: 'danger' as const,
    borderColor: 'danger' as const,
  },
  warning: {
    icon: 'warning-logo' as const,
    themeColor: 'warning' as const,
    borderColor: 'warning' as const,
  },
};

export const Toast = ({
  handleClose,
  variant = 'success',
  'data-qa-id': dataQaId,
  'data-testid': dataTestId,
  description,
  title,
  buttons,
}: ToastProps) => {
  const { t } = useTranslation();
  const validVariant = Object.keys(VARIANTS).includes(variant) ? variant : VARIANTS.success;
  const validDescription =
    description && (typeof description === 'string' || React.isValidElement(description)) ? description : null;
  const validTitle = title && typeof title === 'string' ? title : null;
  const validButtonProps = buttons?.filter(
    (btn) => btn?.id && btn?.label && btn?.to && (typeof btn.label === 'string' || React.isValidElement(btn.label))
  );
  const validHandleClose = typeof handleClose === 'function' ? handleClose : undefined;
  const hasButtons = Boolean(validButtonProps?.length);
  const hasExtraItems = Boolean(validTitle || hasButtons);

  return (
    <BasicContainer
      bgColor="card"
      className="cn_relative cn_atom_p-4 cn_w-80 cn_shadow-sm"
      borderRadius="lg"
      borderPlacement="left"
      borderSize={0.25}
      borderColor={toastIconMap[validVariant].borderColor}
      data-qa-id={dataQaId}
      data-testid={dataTestId}
      data-variant={validVariant}
      mdProps={{ className: 'cn_relative cn_w-100 cn_atom_p-4 cn_shadow-sm' }}
    >
      <ButtonAsWrapper
        aria-label={t('common:button.close')}
        onClick={() => validHandleClose?.()}
        className="cn_absolute cn_atom_p-1"
        style={{ top: hasExtraItems ? '1rem' : '1.15rem', right: '1rem' }}
      >
        <IconWithErrorBoundary name="close" size="sm" themeColor="active" />
      </ButtonAsWrapper>
      <FlexRow
        alignItems={hasExtraItems ? 'flex-start' : 'center'}
        justifyContent="flex-start"
        gap="4"
        className="cn_w-full cn_atom_pr-8"
        data-testid={`${dataTestId}-content`}
        data-qa-id={`${dataQaId}-content`}
        wrap="nowrap"
      >
        <BasicContainer>
          <IconWithErrorBoundary
            name={toastIconMap[validVariant].icon}
            themeColor={toastIconMap[validVariant].themeColor}
            size="md"
          />
        </BasicContainer>
        <FlexCol gap="3">
          {validTitle && <H4Text>{validTitle}</H4Text>}
          {typeof validDescription === 'string' ? <BodyText>{validDescription}</BodyText> : validDescription}
          {hasButtons && (
            <FlexRow gap="2">
              {validButtonProps.map((props) => (
                <ButtonAsLink {...props} key={props.id} onClick={() => validHandleClose?.()} />
              ))}
            </FlexRow>
          )}
        </FlexCol>
      </FlexRow>
    </BasicContainer>
  );
};
